export const PAYMENT_TYPES = Object.freeze({
    CARD: 'CARD',
    LINK: 'LINK',
});

export const ZERO_TOTAL_REASONS = Object.freeze({
    NO_FEES: 'NO_FEES',
    EXEMPTION: 'EXEMPTION',
});

const SELECTABLE_TYPES = Object.freeze([PAYMENT_TYPES.CARD, PAYMENT_TYPES.LINK]);

const OPTION_LABEL_KEYS = Object.freeze({
    [PAYMENT_TYPES.CARD]: 'APPLICATION.PAYMENT.SELECT.ONLINE',
    [PAYMENT_TYPES.LINK]: 'APPLICATION.PAYMENT.SELECT.LINK',
});

const HELP_TEXT_KEYS = Object.freeze({
    [PAYMENT_TYPES.CARD]: 'APPLICATION.PAYMENT.HELP_TEXT_CARD',
    [PAYMENT_TYPES.LINK]: 'APPLICATION.PAYMENT.HELP_TEXT_LINK',
});

const ZERO_TOTAL_LABEL_KEYS = Object.freeze({
    [ZERO_TOTAL_REASONS.NO_FEES]: 'APPLICATION.SUMMARY.PAYMENT_NO_FEES',
    [ZERO_TOTAL_REASONS.EXEMPTION]: 'APPLICATION.SUMMARY.PAYMENT_DISCOUNT',
});

const ZERO_TOTAL_HELP_TEXT_KEYS = Object.freeze({
    [ZERO_TOTAL_REASONS.NO_FEES]: 'APPLICATION.PAYMENT.HELP_TEXT_NO_FEES',
    [ZERO_TOTAL_REASONS.EXEMPTION]: 'APPLICATION.PAYMENT.HELP_TEXT_EXEMPT',
});

const ZERO_TOTAL_SUMMARY_TEXT_KEYS = Object.freeze({
    [ZERO_TOTAL_REASONS.NO_FEES]: Object.freeze({
        pending: 'APPLICATION.SUMMARY.PAYMENT_NO_FEES_TEXT',
        paid: 'APPLICATION.SUMMARY.PAYMENT_NO_FEES_TEXT_SUCCESS',
    }),
    [ZERO_TOTAL_REASONS.EXEMPTION]: Object.freeze({
        pending: 'APPLICATION.SUMMARY.PAYMENT_DISCOUNT_TEXT',
        paid: 'APPLICATION.SUMMARY.PAYMENT_DISCOUNT_TEXT_SUCCESS',
    }),
});

const PAID_DESCRIPTIONS = Object.freeze({
    CARD: Object.freeze({ textKey: 'APPLICATION.SUMMARY.PAYMENT_TYPE_HELP_TEXT_SUCCESS', isStatus: true }),
    LINK: Object.freeze({ textKey: 'APPLICATION.SUMMARY.PAYMENT_TYPE_LINK_HELP_TEXT_SUCCESS', isStatus: true }),
    DISCOUNT: Object.freeze({
        labelKey: 'APPLICATION.SUMMARY.PAYMENT_DISCOUNT',
        textKey: 'APPLICATION.SUMMARY.PAYMENT_DISCOUNT_TEXT_SUCCESS',
        isStatus: true,
    }),
    OP: Object.freeze({
        labelKey: 'APPLICATION.SUMMARY.PAYMENT_TYPE_OP',
        textKey: 'APPLICATION.SUMMARY.PAYMENT_TYPE_HELP_OP_TEXT_SUCCESS',
        isStatus: true,
    }),
});

// The backend registers every zero-total payment as DISCOUNT, exempted or not.
const PAID_NO_FEES_DESCRIPTION = Object.freeze({
    labelKey: ZERO_TOTAL_LABEL_KEYS[ZERO_TOTAL_REASONS.NO_FEES],
    textKey: ZERO_TOTAL_SUMMARY_TEXT_KEYS[ZERO_TOTAL_REASONS.NO_FEES].paid,
    isStatus: true,
});

const isSelectable = (paymentType) => SELECTABLE_TYPES.includes(paymentType);

const buildOptions = ({ selectedType, isLocked, isCardLocked = false }) =>
    SELECTABLE_TYPES.map((type) => {
        const locked = type === PAYMENT_TYPES.CARD && isCardLocked;
        return {
            type,
            labelKey: OPTION_LABEL_KEYS[type],
            selected: type === selectedType,
            disabled: isLocked || locked,
            locked,
        };
    });

const buildState = ({ selectedType, options, description }) => ({
    selectedType,
    options,
    description,
    isMuted: selectedType === null,
    showEmail: selectedType === PAYMENT_TYPES.LINK,
});

const getPaidDescription = (paymentType, zeroTotalReason) => {
    if (paymentType === 'DISCOUNT' && zeroTotalReason === ZERO_TOTAL_REASONS.NO_FEES) {
        return PAID_NO_FEES_DESCRIPTION;
    }
    return PAID_DESCRIPTIONS[paymentType];
};

export const isZeroTotal = (taxes) => taxes != null && (taxes.total ?? 0) === 0;

// The tax summary carries `discount` only when a chosen exemption reduced a non-zero total.
export const getZeroTotalReason = (taxes) => {
    if (!isZeroTotal(taxes)) {
        return null;
    }
    return taxes.discount ? ZERO_TOTAL_REASONS.EXEMPTION : ZERO_TOTAL_REASONS.NO_FEES;
};

export const getZeroTotalSummary = ({ zeroTotalReason, isPaid = false } = {}) => {
    const textKeys = ZERO_TOTAL_SUMMARY_TEXT_KEYS[zeroTotalReason];
    if (!textKeys) {
        return null;
    }
    return {
        labelKey: ZERO_TOTAL_LABEL_KEYS[zeroTotalReason],
        textKey: isPaid ? textKeys.paid : textKeys.pending,
    };
};

export const getPaymentSelectorState = ({ paymentType, isRegister = false, zeroTotalReason = null, isPaid = false } = {}) => {
    if (isPaid) {
        const selectedType = isSelectable(paymentType) ? paymentType : null;
        const paidDescription = getPaidDescription(paymentType, zeroTotalReason);
        return buildState({
            selectedType,
            options: buildOptions({ selectedType, isLocked: true }),
            description: paidDescription ? { ...paidDescription } : null,
        });
    }

    if (zeroTotalReason) {
        return buildState({
            selectedType: null,
            options: buildOptions({ selectedType: null, isLocked: true }),
            description: {
                labelKey: ZERO_TOTAL_LABEL_KEYS[zeroTotalReason],
                textKey: ZERO_TOTAL_HELP_TEXT_KEYS[zeroTotalReason],
                isStatus: true,
            },
        });
    }

    const selectedType = isRegister || paymentType === PAYMENT_TYPES.LINK ? PAYMENT_TYPES.LINK : PAYMENT_TYPES.CARD;
    return buildState({
        selectedType,
        options: buildOptions({ selectedType, isLocked: false, isCardLocked: isRegister }),
        description: { textKey: HELP_TEXT_KEYS[selectedType], isStatus: false },
    });
};
